import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, CheckCircle2, XCircle, Copy, Plus, Send, Info, Loader2, Trophy, ShieldAlert, Eye, ExternalLink, Clock, Download } from 'lucide-react';
import { SiTiktok, SiInstagram, SiYoutube } from 'react-icons/si';
import { useGetChallengeQuery, useJoinChallengeMutation, useSubmitChallengeClipMutation } from '@/generated/graphql';
import { useToast } from '@/hooks/use-toast';
import { PrizePool, PrizeDataStructure } from '@/components/PrizePool';
import { VALIDATE_CHALLENGE_CLIP_HASHTAGS } from '@/queries/queries';
import { wallClockToUTC } from '@/lib/utils';

const PLATFORM_PATTERNS: Array<{ platform: string; label: string; regex: RegExp }> = [
  { platform: 'tiktok', label: 'TikTok', regex: /^https?:\/\/(www\.|vm\.|vt\.)?tiktok\.com\/.+/ },
  { platform: 'instagram', label: 'Instagram', regex: /^https?:\/\/(www\.)?instagram\.com\/.+/ },
  { platform: 'youtube', label: 'YouTube', regex: /^https?:\/\/(www\.)?(youtube\.com\/(shorts\/|watch\?|live\/).+|youtu\.be\/.+)/ },
];

function detectPlatform(url: string): string {
  for (const { platform, regex } of PLATFORM_PATTERNS) {
    if (regex.test(url)) return platform;
  }
  return '';
}

/**
 * Extract a platform-specific video ID from a URL so duplicate detection
 * works even when the same video is submitted with different URL formats.
 */
function extractVideoId(url: string, platform: string): string {
  try {
    const parsed = new URL(url);
    if (platform === 'instagram') {
      // /reel/CODE, /reels/CODE, /p/CODE, /tv/CODE
      const match = parsed.pathname.match(/\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/);
      return match ? match[2] : url;
    }
    if (platform === 'tiktok') {
      // /@user/video/ID or /video/ID or vm.tiktok.com/CODE
      const videoMatch = parsed.pathname.match(/\/video\/(\d+)/);
      if (videoMatch) return videoMatch[1];
      // Short URLs like vm.tiktok.com/CODE — can't resolve, fall back to full path
      if (parsed.hostname === 'vm.tiktok.com' || parsed.hostname === 'vt.tiktok.com') {
        return parsed.pathname.replace(/\/$/, '');
      }
      return url;
    }
    if (platform === 'youtube') {
      if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1).split('/')[0];
      if (parsed.searchParams.has('v')) return parsed.searchParams.get('v')!;
      const match = parsed.pathname.match(/\/(shorts|embed|v|live)\/([^/?]+)/);
      if (match) return match[2];
      return url;
    }
  } catch { /* malformed URL */ }
  return url;
}

function platformLabel(platform: string): string {
  return PLATFORM_PATTERNS.find((p) => p.platform === platform)?.label ?? platform;
}

type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid' | 'error';

interface VideoEntry {
  platform: string;
  url: string;
  validationStatus: ValidationStatus;
  missingHashtags: string[];
  validationError: string;
}

function formatCountdown(seconds: number): string {
  const abs = Math.abs(seconds);
  const d = Math.floor(abs / 86400);
  const h = Math.floor((abs % 86400) / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

const Challenge = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { id } = useParams<{ id: string }>();
  const { data, loading, refetch } = useGetChallengeQuery({ variables: { id: id! }, skip: !id });
  const [joinChallenge] = useJoinChallengeMutation();
  const [submitChallengeClip] = useSubmitChallengeClipMutation();
  const [validateHashtags] = useMutation(VALIDATE_CHALLENGE_CLIP_HASHTAGS);

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState(false);
  const [isJoinDialogOpen, setIsJoinDialogOpen] = useState(false);
  const [isSubmitConsentDialogOpen, setIsSubmitConsentDialogOpen] = useState(false);
  const [joinConsented, setJoinConsented] = useState(false);
  const [submitConsented, setSubmitConsented] = useState(false);
  const [joining, setJoining] = useState(false);
  const [rankingCountdowns, setRankingCountdowns] = useState<Record<string, number>>({});
  const [rankingStartCountdowns, setRankingStartCountdowns] = useState<Record<string, number>>({});
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [videos, setVideos] = useState<VideoEntry[]>([{ platform: '', url: '', validationStatus: 'idle', missingHashtags: [], validationError: '' }]);
  const [submitting, setSubmitting] = useState(false);
  const [pendingViewUrls, setPendingViewUrls] = useState<Set<string>>(new Set());
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const challenge = data?.challenge;

  const handleCopyHashtags = async () => {
    if (challenge?.required_hashtags) {
      await navigator.clipboard.writeText(challenge.required_hashtags);
      toast({ title: t('challenge.hashtagsCopied') });
    }
  };

  const handleAddVideo = () => {
    setVideos([...videos, { platform: '', url: '', validationStatus: 'idle', missingHashtags: [], validationError: '' }]);
  };

  const handleRemoveVideo = (index: number) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  const handleVideoChange = (index: number, value: string) => {
    const newVideos = [...videos];
    newVideos[index] = { ...newVideos[index], url: value, platform: detectPlatform(value), validationStatus: 'idle', missingHashtags: [], validationError: '' };
    setVideos(newVideos);
  };

  const handleJoinChallenge = async () => {
    if (!challenge || !joinConsented) return;
    setJoining(true);
    try {
      await joinChallenge({ variables: { challengeId: challenge.id } });
      await refetch();
      setIsJoinDialogOpen(false);
      setJoinConsented(false);
      toast({ title: t('challenge.joined') });
    } catch (err: any) {
      toast({ title: err.message, variant: 'destructive' });
    } finally {
      setJoining(false);
    }
  };

  const handleOpenSubmitDialog = () => {
    const hasClips = (myParticipation?.challenge_clips ?? []).length > 0;
    if (!hasClips) {
      setSubmitConsented(false);
      setIsSubmitConsentDialogOpen(true);
    } else {
      setIsSubmitDialogOpen(true);
    }
  };

  const handleSubmitConsentConfirm = () => {
    setIsSubmitConsentDialogOpen(false);
    setSubmitConsented(false);
    setIsSubmitDialogOpen(true);
  };

  const handleSubmitVideos = async () => {
    if (!challenge) return;
    const validVideos = videos.filter((v) => v.platform && v.url);
    if (validVideos.length === 0) return;

    setSubmitting(true);

    const updatedVideos = videos.map((v) => ({ ...v }));
    const validIndices = validVideos.map((v) => videos.indexOf(v));

    // Step 1: platform-specific URL validation (client-side)
    let hasPlatformError = false;
    for (const i of validIndices) {
      const video = updatedVideos[i];
      if (video.platform === 'instagram') {
        try {
          const pathname = new URL(video.url).pathname;
          if (!/(\/|^)(reel|reels|p)\//.test(pathname)) {
            updatedVideos[i] = { ...updatedVideos[i], validationStatus: 'invalid', validationError: t('challenge.instagramMustBeReel') };
            hasPlatformError = true;
          }
        } catch { /* malformed URL already blocked by detectPlatform */ }
      }
    }
    if (hasPlatformError) {
      setVideos([...updatedVideos]);
      setSubmitting(false);
      return;
    }

    // Step 2: hashtag validation (skip Instagram — validated manually later)
    if (challenge.required_hashtags) {
      const indicesToValidate = validIndices.filter((i) => updatedVideos[i].platform !== 'instagram');
      const instagramIndices = validIndices.filter((i) => updatedVideos[i].platform === 'instagram');

      // Mark Instagram videos as valid immediately (will be validated manually)
      instagramIndices.forEach((i) => {
        updatedVideos[i] = { ...updatedVideos[i], validationStatus: 'valid', missingHashtags: [], validationError: '' };
      });

      if (indicesToValidate.length > 0) {
        indicesToValidate.forEach((i) => {
          updatedVideos[i] = { ...updatedVideos[i], validationStatus: 'validating' };
        });
        setVideos([...updatedVideos]);

        await Promise.all(
          indicesToValidate.map(async (i) => {
            const video = updatedVideos[i];
            try {
              const { data } = await validateHashtags({
                variables: {
                  input: {
                    challenge_id: challenge.id,
                    platform: video.platform,
                    url: video.url,
                  },
                },
              });
              const result = data?.validateChallengeClipHashtags;
              updatedVideos[i] = {
                ...updatedVideos[i],
                validationStatus: result?.hashtags_valid ? 'valid' : 'invalid',
                missingHashtags: result?.missing_hashtags ?? [],
                validationError: '',
              };
            } catch {
              updatedVideos[i] = { ...updatedVideos[i], validationStatus: 'error', missingHashtags: [], validationError: '' };
            }
          })
        );
      }

      setVideos([...updatedVideos]);

      const hasInvalid = validIndices.some((i) => updatedVideos[i].validationStatus === 'invalid' || updatedVideos[i].validationStatus === 'error');
      if (hasInvalid) {
        setSubmitting(false);
        return;
      }
    }

    let hasSubmitError = false;
    const successfulUrls: string[] = [];

    for (let i = 0; i < validVideos.length; i++) {
      const video = validVideos[i];
      const videoIndex = validIndices[i];
      try {
        await submitChallengeClip({
          variables: {
            input: {
              challenge_id: challenge.id,
              platform: video.platform,
              url: video.url,
            },
          },
        });
        successfulUrls.push(video.url);
      } catch (err: any) {
        hasSubmitError = true;
        const message = err.graphQLErrors?.[0]?.message ?? err.message;
        updatedVideos[videoIndex] = { ...updatedVideos[videoIndex], validationStatus: 'invalid', validationError: message };
      }
    }

    if (hasSubmitError) {
      setVideos([...updatedVideos]);
      toast({ title: t('challenge.submissionFailed'), variant: 'destructive' });
      if (successfulUrls.length > 0) {
        await refetch();
        startViewPolling(successfulUrls);
      }
    } else {
      await refetch();
      setIsSubmitDialogOpen(false);
      startViewPolling(successfulUrls);
      setVideos([{ platform: '', url: '', validationStatus: 'idle', missingHashtags: [], validationError: '' }]);
      toast({ title: t('challenge.videosSubmitted') });
    }

    setSubmitting(false);
  };

  const challengeRankings = data?.challenge?.rankings || [];
  const rankingKey = challengeRankings
    .map(r => `${r.id}:${r.is_consolidated}:${r.time_until_start_seconds}:${r.time_until_end_seconds}`)
    .join(',');
  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);

    const initialEnd: Record<string, number> = {};
    const initialStart: Record<string, number> = {};
    for (const r of challengeRankings) {
      if (!r.is_consolidated) {
        initialEnd[r.id] = r.time_until_end_seconds ?? 0;
        if ((r.time_until_start_seconds ?? 0) > 0) {
          initialStart[r.id] = r.time_until_start_seconds!;
        }
      }
    }
    setRankingCountdowns(initialEnd);
    setRankingStartCountdowns(initialStart);

    if (Object.keys(initialEnd).length > 0 || Object.keys(initialStart).length > 0) {
      countdownRef.current = setInterval(() => {
        setRankingCountdowns((prev) => {
          const next = { ...prev };
          for (const id of Object.keys(next)) next[id] = next[id] - 1;
          return next;
        });
        setRankingStartCountdowns((prev) => {
          const next = { ...prev };
          for (const id of Object.keys(next)) next[id] = next[id] - 1;
          return next;
        });
      }, 1000);
    }
    return () => { if (countdownRef.current) clearInterval(countdownRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingKey]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    if (pollingTimeoutRef.current) { clearTimeout(pollingTimeoutRef.current); pollingTimeoutRef.current = null; }
    setPendingViewUrls(new Set());
  }, []);

  const startViewPolling = useCallback((urls: string[]) => {
    setPendingViewUrls(new Set(urls));
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    pollingRef.current = setInterval(() => { refetch(); }, 5000);
    pollingTimeoutRef.current = setTimeout(stopPolling, 60000);
  }, [refetch, stopPolling]);

  // Stop polling once all pending clips have views
  useEffect(() => {
    if (pendingViewUrls.size === 0) return;
    const clips = data?.challenge?.my_participation?.challenge_clips ?? [];
    const allDone = [...pendingViewUrls].every((url) => {
      const clip = clips.find((c) => c.url === url);
      return clip && parseInt(clip.total_views || '0', 10) > 0;
    });
    if (allDone) stopPolling();
  }, [data, pendingViewUrls, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  if (loading) {
    return (
      <Layout>
        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-muted rounded w-2/3" />
              <div className="h-6 bg-muted rounded w-1/2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-48 bg-muted rounded-xl" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!challenge) {
    return (
      <Layout>
        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto text-center py-20">
            <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">{t('challenge.noActiveChallenge')}</h2>
            <p className="text-muted-foreground">{t('challenge.checkBackLater')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const rulesAllowed = (challenge.rules_allowed as string[] | null) || [];
  const rulesForbidden = (challenge.rules_forbidden as string[] | null) || [];
  const captionExamples = (challenge.caption_examples as string[] | null) || [];
  const prizeData = (challenge.prize_data as PrizeDataStructure | null);
  const rankings = challenge.rankings || [];
  const myParticipation = challenge.my_participation;
  const challengeTz = (challenge.rankings && challenge.rankings[0]?.timezone) || 'America/Sao_Paulo';
  const hasEnded = challenge.end_date ? wallClockToUTC(challenge.end_date, challengeTz) < new Date() : false;
  const alreadySubmittedIds = new Set(
    (myParticipation?.challenge_clips ?? []).map((c) => `${c.platform}:${extractVideoId(c.url, c.platform)}`),
  );

  return (
    <Layout>
      <div className="flex-1 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold">
              {t('challenge.title')}{' '}
              <span className="text-primary">AutoClipper</span>
              {t('challenge.titleSuffix')}
            </h1>
            <p className="text-muted-foreground mt-2 text-lg">
              {challenge.description || t('challenge.subtitle')}
            </p>
          </div>

          {/* Warning Banner */}
          {challenge.warning_message && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-amber-200">
                <span className="font-semibold text-amber-400">{t('challenge.warningPrefix')}</span>{' '}
                {challenge.warning_message}
              </p>
            </div>
          )}

          {/* Rules Cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${challenge.rules_attention ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
            {/* PODE */}
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                  <h3 className="font-bold text-base">{t('challenge.rules.allowed')}</h3>
                </div>
                <ul className="space-y-2">
                  {rulesAllowed.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-emerald-500 mt-1.5 shrink-0">•</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* NÃO PODE */}
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <XCircle className="w-6 h-6 text-red-500" />
                  <h3 className="font-bold text-base">{t('challenge.rules.forbidden')}</h3>
                </div>
                <ul className="space-y-2">
                  {rulesForbidden.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="text-red-500 mt-1.5 shrink-0">•</span>
                      {rule}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* ATENÇÃO */}
            {challenge.rules_attention && (
              <Card className="bg-card border-border">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-6 h-6 text-amber-500" />
                    <h3 className="font-bold text-base">{t('challenge.rules.attention')}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">{challenge.rules_attention}</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Hashtags + Caption Examples */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Required Hashtags */}
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">📌</span>
                  <h3 className="font-bold text-lg">{t('challenge.requiredHashtags')}</h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('challenge.hashtagsDescription')}
                </p>
                <div className="bg-background rounded-lg p-4 mb-4">
                  <pre className="text-primary text-sm whitespace-pre-wrap font-mono">
                    {challenge.required_hashtags}
                  </pre>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleCopyHashtags}
                >
                  <Copy className="w-4 h-4 mr-2" />
                  {t('challenge.copyHashtags')}
                </Button>
              </CardContent>
            </Card>

            {/* Caption Examples */}
            <Card className="bg-card border-border">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">💡</span>
                  <h3 className="font-bold text-lg">{t('challenge.captionExamples')}</h3>
                </div>
                <div className="space-y-2">
                  {captionExamples.map((example, i) => (
                    <div key={i} className="bg-background rounded-lg p-3">
                      <p className="text-sm">{example}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Requirements bar */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-1.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {t('challenge.watermarkRequired')}
                </span>
                <span className="flex items-center gap-1.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {t('challenge.freeDescription')}
                </span>
                <span className="flex items-center gap-1.5 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  {t('challenge.hashtagsRequired')}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative w-16 h-16 rounded-md overflow-hidden border border-border bg-[length:8px_8px] bg-[image:linear-gradient(45deg,hsl(var(--muted))_25%,transparent_25%,transparent_75%,hsl(var(--muted))_75%),linear-gradient(45deg,hsl(var(--muted))_25%,transparent_25%,transparent_75%,hsl(var(--muted))_75%)] bg-[position:0_0,4px_4px]">
                  <img
                    src="/images/autoclipper-watermark.png"
                    alt={t('challenge.watermarkRequired')}
                    className="w-full h-full object-contain"
                  />
                </div>
                <Button variant="outline" size="sm" asChild>
                  <a href="/images/autoclipper-watermark.png" download="autoclipper-watermark.png">
                    <Download className="w-4 h-4" />
                    {t('challenge.downloadWatermark')}
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Prize Pool */}
          {prizeData && prizeData.tabs && prizeData.tabs.length > 0 && (
            <PrizePool
              data={prizeData}
              title={t('challenge.prizePool')}
              subtitle={t('challenge.prizePoolSubtitle')}
            />
          )}

          {/* Join / Submit section */}
          <Card className="bg-card border-border">
            <CardContent className="p-5">
              {!myParticipation ? (
                <div className="text-center py-6">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-primary" />
                  <h3 className="text-xl font-bold mb-2">{t('challenge.joinTitle')}</h3>
                  <p className="text-muted-foreground mb-4">{t('challenge.joinDescription')}</p>
                  {hasEnded || challenge.is_accepting_participants === false ? (
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground text-sm font-medium">
                      <XCircle className="w-4 h-4" />
                      {hasEnded ? t('challenge.submissionsClosed') : t('challenge.notAcceptingParticipants')}
                    </div>
                  ) : (
                    <Button variant="gradient" size="lg" onClick={() => { setJoinConsented(false); setIsJoinDialogOpen(true); }}>
                      {t('challenge.joinButton')}
                    </Button>
                  )}
                </div>
              ) : (
                <div>
                  {/* Disqualification notice */}
                  {myParticipation.disqualified && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3 mb-4">
                      <ShieldAlert className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold text-red-400">{t('challenge.participationDisqualified')}</p>
                        {myParticipation.disqualification_reason && (
                          <p className="text-sm text-red-300 mt-0.5">
                            {t('challenge.participationDisqualifiedReason', { reason: myParticipation.disqualification_reason })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">{t('challenge.myVideos')}</h3>
                    {!myParticipation.disqualified && !hasEnded && (
                      <Button variant="gradient" onClick={handleOpenSubmitDialog}>
                        <Send className="w-4 h-4 mr-2" />
                        {t('challenge.submitVideo')}
                      </Button>
                    )}
                    {hasEnded && (
                      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-muted text-muted-foreground text-sm font-medium">
                        <XCircle className="w-4 h-4" />
                        {t('challenge.submissionsClosed')}
                      </div>
                    )}
                  </div>
                  {myParticipation.challenge_clips && myParticipation.challenge_clips.length > 0 ? (
                    <div className="space-y-2.5">
                      {myParticipation.challenge_clips.map((clip) => {
                        type PlatformMeta = { icon: React.ReactNode; color: string };
                        const platformMeta: Record<string, PlatformMeta> = {
                          tiktok:    { icon: <SiTiktok />,    color: 'text-[#ee1d52]' },
                          instagram: { icon: <SiInstagram />, color: 'text-[#e1306c]' },
                          youtube:   { icon: <SiYoutube />,   color: 'text-[#ff0000]' },
                        };
                        const pm = platformMeta[clip.platform?.toLowerCase() ?? ''] ?? { icon: null, color: 'text-muted-foreground' };
                        const isPending = clip.admin_approval !== 'approved' || clip.validated_hashtags !== 'validated';
                        return (
                          <div
                            key={clip.id}
                            className={`flex items-center gap-3 px-4 py-3 bg-background/60 rounded-xl border border-border/40 transition-colors hover:bg-background/90 ${clip.admin_approval === 'disapproved' ? 'opacity-60' : ''}`}
                          >
                            {/* Platform indicator */}
                            <div className={`shrink-0 flex items-center gap-1.5 ${pm.color}`}>
                              <span className="text-base leading-none">{pm.icon}</span>
                              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                                {clip.platform}
                              </span>
                            </div>

                            {/* URL + status badges */}
                            <div className="flex-1 min-w-0">
                              <a
                                href={clip.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                              >
                                <span className="truncate">{clip.url}</span>
                                <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover:opacity-60 transition-opacity" />
                              </a>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-[11px] text-muted-foreground/60">
                                  {t('challenge.clipSubmittedAt', {
                                    date: new Date(clip.createdAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                                  })}
                                </span>
                                {clip.admin_approval === 'disapproved' && (
                                  <>
                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400">
                                      <ShieldAlert className="w-3 h-3" />
                                      {t('challenge.clipDisqualified')}
                                    </span>
                                    {clip.approval_reason && (
                                      <span className="text-xs text-red-400/70 truncate">{clip.approval_reason}</span>
                                    )}
                                  </>
                                )}
                                {clip.admin_approval !== 'disapproved' && isPending && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400">
                                    <Clock className="w-3 h-3" />
                                    {t('challenge.clipPendingValidation')}
                                  </span>
                                )}
                                {!isPending && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {t('challenge.clipApproved')}
                                  </span>
                                )}
                                {clip.validated_hashtags === 'validated' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
                                    <CheckCircle2 className="w-3 h-3" />
                                    {t('challenge.clipHashtagsValidated')}
                                  </span>
                                )}
                                {clip.validated_hashtags === 'invalidated' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400">
                                    <XCircle className="w-3 h-3" />
                                    {t('challenge.clipHashtagsInvalidated')}
                                  </span>
                                )}
                                {clip.validated_hashtags === 'neutral' && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-400">
                                    <Clock className="w-3 h-3" />
                                    {t('challenge.clipHashtagsPending')}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Views — hidden while pending validation */}
                            <div className="shrink-0">
                              {isPending ? (
                                <span className="flex items-center gap-1.5 text-xs text-muted-foreground/50">
                                  <Eye className="w-3.5 h-3.5" />
                                  —
                                </span>
                              ) : (!clip.challenge_clip_views || clip.challenge_clip_views.length === 0) ? (
                                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  {t('challenge.checkingViews')}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1.5 text-sm font-semibold tabular-nums">
                                  <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                                  {parseInt(clip.total_views || '0', 10).toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      <p className="text-[11px] text-muted-foreground/50 mt-2 flex items-center gap-1">
                        <Info className="w-3 h-3 shrink-0" />
                        {t('challenge.viewsUpdatedDaily')}
                      </p>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-4">
                      {t('challenge.noVideosYet')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Rankings */}
          {rankings.length > 0 && (
            <div className="space-y-4">
              {rankings.map((r) => {
                const countdown = rankingCountdowns[r.id];
                const consolidatedData = (r.ranking_data as Array<{ participation_id: string; user_id: string; user_name?: string; user_profile_picture?: string; total_views: string; clip_count: number }> | null) || [];
                const partialData = r.partial_ranking || [];
                const isFinalized = r.is_consolidated && r.admin_validated;
                const displayData = isFinalized ? consolidatedData : partialData;
                return (
                  <Card key={r.id} className="bg-card border-border">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">{r.name}</h3>
                        {isFinalized ? (
                          <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 font-medium">
                            {t('challenge.rankings.consolidated')}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 font-medium flex items-center gap-1">
                            {r.is_consolidated ? (
                              <>{t('challenge.rankings.consolidating')}</>
                            ) : (rankingStartCountdowns[r.id] ?? 0) > 0 ? (
                              <><Clock className="w-3 h-3" />{t('challenge.rankings.startsIn', { time: formatCountdown(rankingStartCountdowns[r.id]) })}</>
                            ) : countdown != null && countdown > 0 ? (
                              <><Loader2 className="w-3 h-3 animate-spin" />{t('challenge.rankings.countdown', { time: formatCountdown(countdown) })}</>
                            ) : (
                              <><Loader2 className="w-3 h-3 animate-spin" />{t('challenge.rankings.consolidating')}</>
                            )}
                          </span>
                        )}
                      </div>
                      {displayData.length > 0 ? (
                        <div className="space-y-2">
                          {displayData.map((entry, i) => (
                            <div key={entry.participation_id} className="flex items-center justify-between py-3 px-4 bg-background rounded-lg">
                              <div className="flex items-center gap-3">
                                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                                  i === 0 ? 'bg-amber-500 text-black' :
                                  i === 1 ? 'bg-gray-400 text-black' :
                                  i === 2 ? 'bg-amber-700 text-white' :
                                  'bg-muted text-muted-foreground'
                                }`}>
                                  {i + 1}
                                </span>
                                <span className="font-medium">{entry.user_name || t('challenge.anonymous')}</span>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-sm text-muted-foreground">
                                  {entry.clip_count} {entry.clip_count === 1 ? t('challenge.clip') : t('challenge.clips')}
                                </span>
                                <span className="font-bold text-primary">
                                  {parseInt(entry.total_views || '0', 10).toLocaleString()} {t('challenge.views')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('challenge.rankings.pendingDescription')}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Join Challenge Dialog */}
      <Dialog open={isJoinDialogOpen} onOpenChange={(open) => { setIsJoinDialogOpen(open); if (!open) setJoinConsented(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{t('challenge.joinConsentTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {rulesAllowed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold text-sm">{t('challenge.rules.allowed')}</span>
                </div>
                <ul className="space-y-1 pl-6">
                  {rulesAllowed.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground list-disc">{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {rulesForbidden.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-sm">{t('challenge.rules.forbidden')}</span>
                </div>
                <ul className="space-y-1 pl-6">
                  {rulesForbidden.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground list-disc">{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {challenge?.rules_attention && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="font-semibold text-sm text-amber-400">{t('challenge.rules.attention')}</span>
                </div>
                <p className="text-sm text-amber-200 pl-6">{challenge.rules_attention}</p>
              </div>
            )}
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="join-consent"
              checked={joinConsented}
              onCheckedChange={(checked) => setJoinConsented(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="join-consent" className="text-sm cursor-pointer text-muted-foreground">
              {t('challenge.joinConsentCheckbox')}
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="gradient"
              className="w-full"
              disabled={!joinConsented || joining}
              onClick={handleJoinChallenge}
            >
              {joining ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trophy className="w-4 h-4 mr-2" />}
              {t('challenge.joinConsentButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Consent Dialog (first video only) */}
      <Dialog open={isSubmitConsentDialogOpen} onOpenChange={(open) => { setIsSubmitConsentDialogOpen(open); if (!open) setSubmitConsented(false); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{t('challenge.submitConsentTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {rulesAllowed.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="font-semibold text-sm">{t('challenge.rules.allowed')}</span>
                </div>
                <ul className="space-y-1 pl-6">
                  {rulesAllowed.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground list-disc">{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {rulesForbidden.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <span className="font-semibold text-sm">{t('challenge.rules.forbidden')}</span>
                </div>
                <ul className="space-y-1 pl-6">
                  {rulesForbidden.map((rule, i) => (
                    <li key={i} className="text-sm text-muted-foreground list-disc">{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {challenge?.rules_attention && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                  <span className="font-semibold text-sm text-amber-400">{t('challenge.rules.attention')}</span>
                </div>
                <p className="text-sm text-amber-200 pl-6">{challenge.rules_attention}</p>
              </div>
            )}
          </div>

          <div className="flex items-start gap-3 pt-2">
            <Checkbox
              id="submit-consent"
              checked={submitConsented}
              onCheckedChange={(checked) => setSubmitConsented(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="submit-consent" className="text-sm cursor-pointer text-muted-foreground">
              {t('challenge.submitConsentCheckbox')}
            </label>
          </div>

          <DialogFooter>
            <Button
              variant="gradient"
              className="w-full"
              disabled={!submitConsented}
              onClick={handleSubmitConsentConfirm}
            >
              <Send className="w-4 h-4 mr-2" />
              {t('challenge.submitConsentButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Video Dialog */}
      <Dialog open={isSubmitDialogOpen} onOpenChange={setIsSubmitDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-xl font-bold">{t('challenge.submitVideoTitle')}</span>
                <span className="text-2xl">🏆</span>
              </div>
              <p className="text-sm text-muted-foreground font-normal">
                {t('challenge.submitVideoSubtitle')}
              </p>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {videos.map((video, index) => {
              const videoKey = video.url && video.platform ? `${video.platform}:${extractVideoId(video.url, video.platform)}` : '';
              const isAlreadySubmitted = !!videoKey && alreadySubmittedIds.has(videoKey);
              const isBatchDuplicate = !!videoKey && videos.some((v, j) => j !== index && v.platform && v.url && `${v.platform}:${extractVideoId(v.url, v.platform)}` === videoKey);
              const isDuplicate = isAlreadySubmitted || isBatchDuplicate;
              return (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">{t('challenge.video')} {index + 1}</span>
                    {videos.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveVideo(index)} className="text-red-400 h-6 px-2">
                        ✕
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Input
                      placeholder="https://..."
                      value={video.url}
                      onChange={(e) => handleVideoChange(index, e.target.value)}
                      className={(video.url && !video.platform) || isDuplicate ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {video.url && video.platform && !isDuplicate && (
                      <span className="inline-block text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                        {platformLabel(video.platform)}
                      </span>
                    )}
                    {video.url && !video.platform && (
                      <p className="text-xs text-red-400">{t('challenge.invalidUrl')}</p>
                    )}
                    {isDuplicate && (
                      <p className="flex items-center gap-1 text-xs text-red-400">
                        <XCircle className="w-3.5 h-3.5 shrink-0" />
                        {isAlreadySubmitted ? t('challenge.urlAlreadySubmitted') : t('challenge.urlDuplicate')}
                      </p>
                    )}
                    {video.url && video.platform && video.validationStatus !== 'idle' && (
                      <div className="flex items-center gap-2 pt-1">
                        {video.validationStatus === 'validating' && (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t('challenge.hashtagsValidating')}
                          </span>
                        )}
                        {video.validationStatus === 'valid' && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t('challenge.hashtagsValid')}
                          </span>
                        )}
                        {video.validationStatus === 'invalid' && (
                          <span className="flex items-center gap-1 text-xs text-red-400">
                            <XCircle className="w-3.5 h-3.5" />
                            {video.validationError || (video.missingHashtags.length > 0
                              ? t('challenge.hashtagsMissing', { tags: video.missingHashtags.join(', ') })
                              : t('challenge.hashtagsInvalid'))}
                          </span>
                        )}
                        {video.validationStatus === 'error' && (
                          <span className="flex items-center gap-1 text-xs text-amber-400">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            {t('challenge.hashtagsValidationError')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <Button variant="outline" className="w-full border-dashed" onClick={handleAddVideo}>
              <Plus className="w-4 h-4 mr-2" />
              {t('challenge.addAnotherVideo')}
            </Button>

            <div className="space-y-2">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-400">{t('challenge.duplicateWarning')}</span>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-sm text-amber-400">{t('challenge.watermarkWarning')}</span>
              </div>
            </div>

          </div>

          <DialogFooter>
            <Button
              variant="gradient"
              className="w-full"
              disabled={
                submitting ||
                videos.every((v) => !v.platform || !v.url) ||
                videos.some((v) => v.url && !v.platform) ||
                videos.some((v) => v.validationStatus === 'invalid' || v.validationStatus === 'error') ||
                videos.some((v, i) => {
                  if (!v.url || !v.platform) return false;
                  const key = `${v.platform}:${extractVideoId(v.url, v.platform)}`;
                  return alreadySubmittedIds.has(key) || videos.some((w, j) => j !== i && w.platform && w.url && `${w.platform}:${extractVideoId(w.url, w.platform)}` === key);
                })
              }
              onClick={handleSubmitVideos}
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? t('challenge.submitting') : t('challenge.submitButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Challenge;
